<template>
    <div class="replacement-editor-merc-root flex-root-column">
        <div v-if="viewMode !== 'editor'" class="replacement-editor-header">
            <div class="editor-header-close"><img :src="require('@/assets/icons/replacements/close.svg')" alt="close" class="close-button" @click="closeEditor"></div>
            <div class="editor-header-text"><span class="bold">MERC+</span> CEDEX Replacement</div>
        </div>
        <div class="editor-box replacement-editor-box">
            <div class="editor-line slim-editor-line">
                <v-checkbox
                    v-model="mappingDisabled"
                    :label="$i18n.t('Disable default mapping')"
                    dense
                    color="var(--sc-blue)"
                ></v-checkbox>
            </div>
            <div class="editor-line">
                <div class="editor-block left-block">
                    <div v-if="!isMachinery" class="input-field">
                        <div class="label required">{{$t('Survey Type')}}</div>
                        <v-select
                            class="select ribbon-select-select multi-select"
                            v-model="surveyType"
                            :items="dataWithDependencies('surveyType', {equipment: equipment})"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :multiple="viewMode !== 'editor'"
                            :menu-props="{maxHeight: viewMode === 'editor' ? 300 : defaultListHeight}"
                        />
                        <div class="label required">{{$t('Container Height')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="containerHeight"
                            :items="selectableValues.containerHeights"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: viewMode === 'editor' ? 300 : defaultListHeight}"
                        />
                    </div>
                    <div v-if="isMachinery" class="input-field">
                        <div class="label required">{{$t('Service Type')}}</div>
                        <v-select
                            class="select ribbon-select-select service-type"
                            v-model="serviceType"
                            :items="selectableValues.serviceTypes"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :multiple="viewMode !== 'editor'"
                            :menu-props="{maxHeight: viewMode === 'editor' ? 300 : defaultListHeight}"
                        />
                    </div>
                </div>
                <div class="editor-block right-block">
                    <div class="input-field position-relative">
                        <div class="label">{{$t('Mode')}}</div>
                        <v-text-field
                            class="select ribbon-select-select"
                            v-model="mode"
                            :readonly="true"
                            item-color="var(--sc-blue-dark)"
                        />
                        <img class="icon" src="@/assets/icons/replacements/info.svg" @click="showModeExceptionHint" :title="modeExceptionHint" :alt="modeExceptionHint"/>
                    </div>
                </div>
            </div>
            <div class="editor-line">
                <div class="editor-block left-block">
                    <div class="cedex-code-caption">CEDEX Code</div>
                </div>
                <div class="editor-block right-block">
                    <div class="cedex-code-caption">STS Code</div>
                </div>
                <div class="editor-block left-block">
                    <div v-if="isMachinery" class="input-field">
                        <div class="label required">{{$t('Machinery Manufacturer')}}</div>
                        <v-select
                            class="select ribbon-select-select multi-select"
                            style="height: 30px !important;"
                            v-model="machineryManufacturer"
                            :items="selectableValues.machineryManufacturers"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :multiple="viewMode !== 'editor'"
                            :menu-props="{maxHeight: viewMode === 'editor' ? 300 : defaultListHeight}"
                        />
                    </div>

                    <div v-if="isMachinery" class="input-field">
                        <div class="label">{{$t('Machinery Type')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="machineryType"
                            :items="selectableMachineryTypes"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                            :class="{disabled: !showMachineryType}"
                            :disabled="!showMachineryType"
                        />
                    </div>
                    <div v-else class="input-field">
                        <div class="label required">{{$t('Position')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="viewTypeWithPositionType"
                            :items="selectableViewTypes"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                            @change="setViewType"
                        />
                    </div>
                </div>

                <div class="editor-block right-block">
                    <div class="input-field">
                        <div class="label">{{$t('Code 1 (' + (this.isMachinery ? 'Model Type' : 'Position') + ')')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="stsCode1"
                            :items="selectableStsCode1"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>
                </div>
            </div>
            <div class="editor-line">
                <div class="editor-block left-block">
                    <div class="input-field">
                        <div class="label required">{{$t('Component Code')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="componentCode"
                            :items="dataWithDependencies('componentCode', this.isMachinery ? {equipment: equipment} : {equipment: equipment, surveyType: surveyType, viewType: viewType})"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>

                    <div v-if="!isMachinery" class="input-field">
                        <div class="label" :class="{disabled: !showDetailedPosition}">{{$t('Detailed Position')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            :class="{disabled: !showDetailedPosition}"
                            :disabled="!showDetailedPosition"
                            v-model="detailedPosition"
                            :items="selectableValues.detailedPositionsByComponent[componentCode]"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>

                    <div class="input-field">
                        <div class="label">{{$t('Component Tag')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="componentTag"
                            :items="selectableComponentTags"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>

                    <div v-if="isMachinery && refrigerantComponents.includes(componentCode)" class="input-field">
                        <div class="label">{{$t('Refrigerant Type')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="refrigerant"
                            :items="selectableValues.refrigerants"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>
                </div>
                <div class="editor-block right-block">
                    <div class="input-field">
                        <div class="label">{{$t('Code 2 (Component)')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="stsCode2"
                            :items="selectableStsCode2"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>
                </div>
            </div>

            <div class="editor-line">
                <div class="editor-block left-block">
                    <div class="input-field">
                        <div class="label">{{$t('Damage Tag')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="damageTag"
                            :items="selectableDamageTags"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>
                </div>
                <div class="editor-block right-block" />
            </div>

            <div class="editor-line">
                <div class="editor-block left-block">
                    <div class="input-field">
                        <div class="label required">{{$t('Repair Code')}}</div>
                        <v-select
                            class="select ribbon-select-select multi-select"
                            style="height: 30px !important;"
                            v-model="repairCode"
                            :items="dataWithDependencies('repairCode', {equipment: equipment, surveyType: surveyType, componentCode: componentCode})"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :multiple="viewMode !== 'editor'"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>
                    <div class="input-field">
                        <div class="label">{{$t('Repair Tag')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="repairTag"
                            :items="selectableRepairTags"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>
                </div>
                <div class="editor-block right-block">
                    <div class="input-field">
                        <div class="label">{{$t('Code 3 (Repair)')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="stsCode3"
                            :items="selectableStsCode3"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>
                </div>
            </div>

            <div class="editor-line">
                <div class="editor-block left-block">
                    <div class="input-field">
                        <div class="label">{{$t('Metric Type')}}</div>
                        <v-select
                            class="ribbon-select-select length-type"
                            v-model="metricType"
                            :items="metricTypes"
                            item-color="var(--sc-blue-dark)"
                        />
                    </div>
                    <div v-if="metricType === 'Length' || metricType === 'Perimeter'" class="input-line">
                        <div class="input-field length-block">
                            <div class="label">{{$t('Length (mm)')}}</div>
                            <v-select
                                class="ribbon-select-select length-type"
                                v-model="lengthType"
                                :items="compareTypes"
                                :item-value="'id'"
                                :item-text="'name'"
                                :readonly="parseInt(widthFrom) || 0 > 0"
                                item-color="var(--sc-blue-dark)"
                            />
                        </div>
                        <div class="input-field length-block no-label">
                            <v-text-field
                                class="ribbon-select-select length centered-input"
                                :class="{greater: lengthType === 'greater', smaller: lengthType === 'smaller'}"
                                v-model="lengthFrom"
                                item-color="var(--sc-blue-dark)"
                            />
                        </div>
                        <div v-if="lengthType === 'fromTo'" class="input-field length-block no-label">
                            <div>-</div>
                        </div>
                        <div v-if="lengthType === 'fromTo'" class="input-field length-block no-label">
                            <v-text-field
                                class="ribbon-select-select length centered-input"
                                v-model="lengthTo"
                                item-color="var(--sc-blue-dark)"
                            />
                        </div>
                    </div>
                    <!-- Width -->
                    <div v-if="metricType === 'Perimeter'" class="input-line">
                        <div class="input-field length-block">
                            <div class="label">{{$t('Width (mm)')}}</div>
                            <v-select
                                class="ribbon-select-select length-type"
                                v-model="widthType"
                                :items="['equal']"
                                :readonly="true"
                                item-color="var(--sc-blue-dark)"
                            />
                        </div>
                        <div class="input-field length-block no-label">
                            <v-text-field
                                class="ribbon-select-select length centered-input"
                                label=""
                                v-model="widthFrom"
                                item-color="var(--sc-blue-dark)"
                                type="text"
                            />
                        </div>
                        <br />

                        <!-- Perimeter -->
                        <div class="input-field length-block">
                            <div class="label">{{$t('Perimeter')}}</div>
                            <v-select
                                class="ribbon-select-select length-type"
                                v-model="perimeterType"
                                :items="['fromTo']"
                                :readonly="true"
                                item-color="var(--sc-blue-dark)"
                            />
                        </div>
                        <div class="input-field length-block no-label">
                            <v-text-field
                                class="ribbon-select-select length centered-input"
                                v-model="perimeterFrom"
                                item-color="var(--sc-blue-dark)"
                            />
                        </div>
                        <div class="input-field length-block no-label">
                            <div>-</div>
                        </div>
                        <div class="input-field length-block no-label">
                            <v-text-field
                                class="ribbon-select-select length centered-input"
                                v-model="perimeterTo"
                                :readonly="true"
                                item-color="var(--sc-blue-dark)"
                            />
                        </div>
                        <div v-if="hints.perimeter" class="problem">{{$t(hints.perimeter)}}</div>
                    </div>

                    <!-- Quantity -->
                    <div v-if="metricType === 'Quantity'" class="input-line">
                        <div class="input-field length-block">
                            <div v-if="isMachinery" class="label">{{$t('Quantity (kg)')}}</div>
                            <div v-else class="label">{{$t('Quantity')}}</div>
                            <v-select
                                class="ribbon-select-select length-type"
                                v-model="quantityType"
                                :items="compareTypes"
                                item-color="var(--sc-blue-dark)"
                            />
                        </div>
                        <div class="input-field length-block no-label">
                            <v-text-field
                                class="ribbon-select-select length centered-input"
                                :class="{greater: quantityType === 'greater', smaller: quantityType === 'smaller'}"
                                label=""
                                v-model="quantityFrom"
                                item-color="var(--sc-blue-dark)"
                                type="text"
                            />
                        </div>
                        <div v-if="quantityType === 'fromTo'" class="input-field length-block no-label">
                            <div>-</div>
                        </div>
                        <div v-if="quantityType === 'fromTo'" class="input-field length-block no-label">
                            <v-text-field
                                class="ribbon-select-select length centered-input"
                                v-model="quantityTo"
                                item-color="var(--sc-blue-dark)"
                            />
                        </div>
                        <br />
                    </div>
                </div>
                <div class="editor-block right-block">
                    <div class="input-field">
                        <div class="label">{{$t('Code 4 (Size)')}}</div>
                        <v-select
                            class="select ribbon-select-select"
                            v-model="stsCode4"
                            :items="selectableStsCode4"
                            item-value="id"
                            item-text="name"
                            item-color="var(--sc-blue-dark)"
                            :menu-props="{maxHeight: defaultListHeight}"
                        />
                    </div>
                </div>
            </div>

            <div class="editor-line">
                <div class="editor-block">
                    <div class="input-field length-block width-25">
                        <div class="label">{{$t('Max. Quantity')}}</div>
                        <v-text-field
                            class="ribbon-select-select centered-input"
                            v-model="maxQuantity"
                            item-color="var(--sc-blue-dark)"
                        />
                    </div>
                    <div class="input-field length-block width-25">
                        <div class="label condensed">{{$t('Hours per Quantity')}}</div>
                        <v-text-field
                            class="ribbon-select-select centered-input"
                            v-model="wageHoursPerQuantity"
                            item-color="var(--sc-blue-dark)"
                        />
                    </div>
                    <div class="input-field length-block no-label width-25">
                        <RibbonButtonTwoState
                            class="two-state-button"
                            :text="$t('Prep. Time')"
                            :active="preparationTime === '1'"
                            @clicked="togglePreparationTime"
                        />
                    </div>
                </div>
            </div>
            <div class="editor-line">
                <div class="editor-block">
                    <div class="input-field length-block no-label width-25">
                        <RibbonButtonTwoState
                            class="two-state-button"
                            :text="$t('Material Cost')"
                            :active="materialCost === '1'"
                            @clicked="toggleMaterialCost"
                        />
                    </div>
                </div>
                <div class="editor-block" v-show="materialCost === '1'">
                    <div class="input-field length-block width-50">
                        <div class="label">{{$t('Material Cost')}}</div>
                        <v-text-field
                            class="ribbon-select-select centered-input"
                            v-model="materialCostValue"
                            item-color="var(--sc-blue-dark)"
                            maxLength=9
                            @keyup="validateCostValue"
                        />
                    </div>
                    <div class="input-field length-block width-50">
                        <div class="label condensed">{{$t('EUR')}}</div>
                    </div>
                </div>
                <div class="editor-block right-block"></div>
            </div>
        </div>
    </div>
</template>

<script>
import RibbonButtonTwoState from "../../Ribbon/RibbonButtonTwoState";
// eslint-disable-next-line no-unused-vars
import i18n from '../../../i18n';
import equipments from "../../../plugins/constants";
import replacementTrait from "../../../plugins/traits/replacement";

export default {
    name: "ReplacementsMercEditor",
    components: {RibbonButtonTwoState},
    props: {
        viewMode:                     String,
        equipment:                    String,
        stsCodes:                     Object,
        modeReplacements:             Array,
        selectedValues:               Object,
        selectableValues:             Object,
        selectableDependencies:       Object,
        componentTags:                Object,
        damageTags:                   Object,
        repairTags:                   Object,
        detailedPositionsByComponent: Object,
        refrigerantComponents:        Array,
    },
    data() {
        return {
            // given replacement identifier data
            surveyType:            this.viewMode === 'editor'
                                   ? this.selectedValues.surveyType
                                   : (this.selectedValues.surveyType
                                       ? this.selectedValues.surveyType.split(',')
                                       : []
                                   ),
            containerHeight:       this.selectedValues.containerHeight,
            serviceType:           this.viewMode === 'editor'
                                   ? this.selectedValues.serviceType
                                   : (this.selectedValues.serviceType
                                       ? this.selectedValues.serviceType.split(',')
                                       : []
                                   ),
            viewType:              this.selectedValues.viewType,
            positionType:          this.selectedValues.positionType,
            machineryManufacturer: this.viewMode === 'editor'
                                   ? this.selectedValues.machineryManufacturer
                                   : (this.selectedValues.machineryManufacturer
                                       ? this.selectedValues.machineryManufacturer.split(',')
                                       : []
                                   ),
            machineryType:         this.selectedValues.machineryType,
            detailedPosition:      this.selectedValues.detailedPosition,
            componentCode:         this.selectedValues.componentCode,
            componentTag:          this.selectedValues.componentTag,
            refrigerant:           this.selectedValues.refrigerant,
            damageTag:             this.selectedValues.damageTag,
            repairCode:            this.viewMode === 'editor'
                                   ? this.selectedValues.repairCode
                                   : (this.selectedValues.repairCode
                                       ? this.selectedValues.repairCode.split(',')
                                       : []
                                   ),
            repairTag:             this.selectedValues.repairTag,
            lengthType:            this.selectedValues.lengthType,
            lengthFrom:            this.selectedValues.lengthFrom,
            lengthTo:              this.selectedValues.lengthTo,
            widthType:             'equal',
            widthFrom:             this.selectedValues.widthFrom,
            widthTo:               this.selectedValues.widthTo,
            perimeterType:         'fromTo',
            perimeterFrom:         this.selectedValues.perimeterFrom,
            perimeterTo:           this.selectedValues.perimeterTo,

            quantityType:          this.selectedValues.quantityType,
            quantityFrom:          this.selectedValues.quantityFrom,
            quantityTo:            this.selectedValues.quantityTo,

            // entered replacement data
            stsCode1:              this.selectedValues.stsCode1,
            stsCode2:              this.selectedValues.stsCode2,
            stsCode3:              this.selectedValues.stsCode3,
            stsCode4:              this.selectedValues.stsCode4,
            maxQuantity:           this.selectedValues.maxQuantity,
            wageHoursPerQuantity:  this.selectedValues.wageHoursPerQuantity,
            materialCostValue:     this.selectedValues.materialCostValue,
            materialCost:          this.selectedValues.materialCost,
            preparationTime:       this.selectedValues.preparationTime,
            mappingDisabled:       this.selectedValues.mappingDisabled === '1',

            compareTypes: [
                {id: 'equal', name: 'equal'},
                {id: 'fromTo', name: 'from - to'},
                {id: 'greater', name: 'greater/equal'},
                {id: 'smaller', name: 'smaller/equal'}
            ],
            metricType: (
                !!this.selectedValues.lengthType && !!this.selectedValues.lengthFrom
                    ? (!!this.selectedValues.perimeterType && !!this.selectedValues.perimeterFrom ? 'Perimeter' : 'Length')
                    : (!!this.selectedValues.quantityType && !!this.selectedValues.quantityFrom ? 'Quantity' : 'None')
            ),
            metricTypes: this.equipment === equipments.MACHINERY ? ['None', 'Length', 'Quantity'] : ['None', 'Length', 'Perimeter', 'Quantity'],
            multiSelects: ['surveyType', 'serviceType', 'machineryManufacturer', 'repairCode'],

            viewTypeWithPositionType: this.selectedValues.viewType + (this.selectedValues.positionType ? '-' + this.selectedValues.positionType : ''),

            cachedDependencies: {},

            defaultListHeight: 600,

            hints: {}
        };
    },
    methods: {
        isMultiSelect(field) {
            return this.multiSelects.includes(field);
        },
        closeEditor() {
            if (this.changed) {
                // do some confirmation stuff
            } else {
                this.$emit('closeEditor', []);
            }
        },
        collectData() {
            let surveyType            = this.surveyType            || [];
            let serviceType           = this.serviceType           || [];
            let machineryManufacturer = this.machineryManufacturer || [];
            let repairCode            = this.repairCode            || [];

            // join multiselect columns for editor popup view
            if (this.viewMode !== 'editor') {
                surveyType            = surveyType.join(',')
                serviceType           = serviceType.join(',')
                machineryManufacturer = machineryManufacturer.join(',')
                repairCode            = repairCode.join(',');
            } else {
                if (this.isMachinery) {
                    surveyType = null;
                }
            }

            return {
                id:                    this.selectedValues.id,
                surveyType:            surveyType,
                containerHeight:       this.containerHeight,
                serviceType:           serviceType,
                viewType:              this.viewType,
                positionType:          this.positionType,
                machineryManufacturer: machineryManufacturer,
                machineryType:         this.machineryType,
                detailedPosition:      this.detailedPosition,
                componentCode:         this.componentCode,
                componentTag:          this.componentTag,
                refrigerant:           this.refrigerant,
                damageTag:             this.damageTag,
                repairCode:            repairCode,
                repairTag:             this.repairTag,
                lengthType:            this.lengthType,
                lengthFrom:            this.lengthFrom,
                lengthTo:              this.lengthTo,
                widthType:             this.widthType,
                widthFrom:             this.widthFrom,
                widthTo:               this.widthTo,
                perimeterType:         this.perimeterType,
                perimeterFrom:         this.perimeterFrom,
                perimeterTo:           this.perimeterTo,
                quantityType:          this.quantityType,
                quantityFrom:          this.quantityFrom,
                quantityTo:            this.quantityTo,

                // entered replacement data
                stsCode1:              this.stsCode1,
                stsCode2:              this.stsCode2,
                stsCode3:              this.stsCode3,
                stsCode4:              this.stsCode4,
                maxQuantity:           this.maxQuantity,
                wageHoursPerQuantity:  this.wageHoursPerQuantity,
                materialCostValue:     this.materialCostValue,
                materialCost:          this.materialCost,
                preparationTime:       this.preparationTime,
                mappingDisabled:       this.mappingDisabled ? '1' : '0',
            };
        },
        toggleMaterialCost() {
            this.materialCost    = this.materialCost   === '1' ? '0' : '1';
        },
        togglePreparationTime() {
            this.preparationTime = this.preparationTime === '1' ? '0' : '1';
        },
        calculatePerimeter() {
            this.widthType     = 'equal';

            let length = parseInt(this.lengthFrom) || 0;
            let width  = parseInt(this.widthFrom) || 0;

            delete this.hints.perimeter;

            if (width > 0) {
                this.lengthType = 'equal';
            }

            if (
                length
                && width > 0
            ) {
                this.perimeterTo = 2 * length + 2 * width;
                if (this.perimeterFrom > this.perimeterTo) {
                    this.hints.perimeter = 'Perimeter from is greater than perimeter to.';
                } else if ((parseInt(this.perimeterFrom) || 0) <= 0) {
                    this.hints.perimeter = 'Perimeter from is empty.';
                }
            } else {
                this.perimeterTo = '';
            }
        },
        setViewType() {
            // split local data into view type and position type
            // set the two variables
            if (this.viewTypeWithPositionType.includes('-')) {
                let split = this.viewTypeWithPositionType.split('-');
                this.viewType     = split[0];
                this.positionType = split[1];
            } else {
                this.viewType     = this.viewTypeWithPositionType;
                this.positionType = null;
            }

            // trigger changed event
            this.$emit('changed', this.changed, this.collectData())
        },

        dataWithDependencies(field, dependencies)
        {
            let log = false;
            if (log) {
                console.log('allDep', field, dependencies);
            }

            // set data sources (all values array and selectable dependencies identifier)
            let dependenciesIdentifier;
            let allArrayIdentifier;
            if (field === 'surveyType') {
                dependenciesIdentifier = 'surveyTypes';
                allArrayIdentifier = 'surveyTypes';
            } else if (field === 'serviceType') {
                dependenciesIdentifier = 'serviceType';
                allArrayIdentifier = 'serviceType';
            } else if (field === 'componentCode') {
                dependenciesIdentifier = 'componentCodes';
                allArrayIdentifier = 'componentCodes';
            } else if (field === 'repairCode') {
                if (dependencies.componentCode === '') {
                    dependenciesIdentifier = 'repairCodes_alone';
                } else {
                    dependenciesIdentifier = 'repairCodes_component';
                }
                allArrayIdentifier = 'repairCodes';
            }

            let selectableDependenciesLoaded = typeof this.$parent.selectableDependencies[dependenciesIdentifier] !== 'undefined';

            if (!selectableDependenciesLoaded) {
                if (log) {
                    console.log('selectable dependencies not loaded, use all', this.selectableValues[allArrayIdentifier])
                }
                return this.selectableValues[allArrayIdentifier];
            }

            if (log) {
                console.log('recalculate dependencies', field, dependencies)
            }
            let allValuesArray = [];

            // build dependency key
            // it is used to determine the selectable dependencies
            // example: <equipment>_<serviceType>_<isoCode>
            // there are multiselect column which result in more than one key and dependency
            // that's why the process is so complex
            let dependencyKeys = [];
            let dependencyKeyJoinOrder = [];
            if (this.isMachinery) {
                dependencyKeyJoinOrder = ['equipment', 'serviceType', 'componentCode'];
            } else {
                dependencyKeyJoinOrder = ['equipment', 'surveyType', 'viewType', 'componentCode'];
            }
            let addDependencyKey = function(part) {
                let parts = part.split(',');
                let newDependencyKey = [];
                for (let key in parts) {
                    if (dependencyKeys.length === 0) {
                        newDependencyKey.push(parts[key]); // specific dependency value
                    }
                    for (let index in dependencyKeys) {
                        newDependencyKey.push(dependencyKeys[index] + '_' + parts[key]); // specific dependency value
                    }
                }
                dependencyKeys = newDependencyKey;
            };

            for (let orderIndex in dependencyKeyJoinOrder) {
                let joinPart = dependencies[dependencyKeyJoinOrder[orderIndex]];
                if (Array.isArray(joinPart)) {
                    joinPart = joinPart.join(',');
                }

                if (
                    // value not set
                    typeof joinPart === 'undefined'
                    || joinPart === ''
                    // sometimes the reactive observer is part of the values array and have to be excluded
                    || (
                        typeof joinPart !== 'string'
                        && joinPart !== null
                    )
                ) {
                    continue;
                }

                if (joinPart === null) {
                    // all values for multiselect
                    let realJoinParts = [];

                    for (let partIndex in this[dependencyKeyJoinOrder[orderIndex] + 's']) {
                        realJoinParts.push(partIndex);
                    }
                    joinPart = realJoinParts.join(',');
                }
                addDependencyKey(joinPart)
            }

            if (log) {
                console.log('dependency key', dependencyKeys)
            }
            let cacheKey = dependencyKeys.join('#');
            if (
                this.cachedDependencies[field]
                && this.cachedDependencies[field][cacheKey]
            ) {
                // read from cache
                allValuesArray = this.cachedDependencies[field][cacheKey];
                if (log) {
                    console.log('use cached value', allValuesArray)
                }
            } else {
                // generate data

                // iterate through dependency keys (service type and iso code are multiselects, so multiple keys can exist)
                // each iteration will intersect its values with the previous
                // so only values which exist in each dependency will be able to select
                if (log) {
                    console.log('start reading dependencies - breakpoint');
                }
                for (let key in dependencyKeys) {
                    if (log) {
                        console.log('available values', this.$parent.selectableDependencies[dependenciesIdentifier][dependencyKeys[key]])
                    }

                    // read old values from previous iteration
                    let oldValues  = [...allValuesArray];

                    // reset current values
                    allValuesArray = [];

                    // iterate through every possible value
                    for (let index in this.selectableValues[allArrayIdentifier]) {
                        // check if value can be added
                        if (
                            // empty dependencies (selectable dependencies are not loaded yet, all values will be displayed)
                            !selectableDependenciesLoaded
                            // value exist in current dependency
                            || (
                                this.$parent.selectableDependencies[dependenciesIdentifier][dependencyKeys[key]]
                                && this.$parent.selectableDependencies[dependenciesIdentifier][dependencyKeys[key]].includes(this.selectableValues[allArrayIdentifier][index].id)
                            )
                            // check iteration
                            && (
                                // is first iteration (allow every value)
                                key == 0
                                // check intersection with previous iteration
                                || oldValues.includes(this.selectableValues[allArrayIdentifier][index])
                            )
                        ) {
                            // add value
                            allValuesArray.push(this.selectableValues[allArrayIdentifier][index])
                        }
                    }
                }

                // add value to cache
                if (selectableDependenciesLoaded) {
                    if (typeof this.cachedDependencies[field] === 'undefined') {
                        this.cachedDependencies[field] = {};
                    }
                    this.cachedDependencies[field][cacheKey] = allValuesArray;
                }

                if (log) {
                    console.log('calculated value', allValuesArray)
                }
            }

            // Auto select value, if only one selection is possible
            if (allValuesArray.length === 1) {
                let value = allValuesArray[0].id;
                // distinguish between normal and multi select
                if (
                    this.isMultiSelect(field)
                    && this.viewMode !== 'editor'
                    // prevent reset the value
                    && (
                        !Array.isArray(this[field])
                        || this[field].length !== 1
                        || value !== this[field][0]
                    )
                ) {
                    this[field] = [value];
                } else if(
                    (
                        !this.isMultiSelect(field)
                        || this.viewMode === 'editor'
                    )
                    && this[field] !== value
                ) { // prevent reset the value
                    this[field] = value;
                }
            }

            return allValuesArray;
        },

        getUnmappedStsCodes()
        {
            // returns an object with codes from 0 to 9 with empty names as data for unmapped STS codes.
            let data = {};
            for (let i=0; i<10; i++) {
                data[i] = i;
            }

            return data;
        },
        isStsCodeSelectable(code) {
            let selectableName = 'selectableStsCode' + code;
            let codeName = 'stsCode' + code;
            let found = false;

            this[selectableName].forEach((data) => {
                if (data.id === this[codeName]) {
                    found = true;
                }
            });

            return found;
        },
        fixStsCode1()
        {
            if (this.machineryManufacturer.length > 1) {
                this.stsCode1 = 'X';
            } else if (!this.isStsCodeSelectable(1)) {
                this.stsCode1 = '';
            }
        },
        showModeExceptionHint()
        {
            this.$emit('showMessage', this.modeExceptionHint, 'info', 0, i18n.t('OK'));
        },
        computeTagDependencies(tagType)
        {
            let tags = [];
            let dependencyMap = [
                'equipment',
                this.isMachinery ? 'serviceType'           : 'surveyType',
                this.isMachinery ? 'machineryManufacturer' : 'viewType',
                'componentCode'
            ];
            if (tagType === 'repair') {
                dependencyMap.push('repairCode');
            }

            let tagsObject = this[tagType + 'Tags'];
            Object.keys(tagsObject).forEach(tagId => {
                // tag selectable
                let tagSelectable = this.processTagDependencies(dependencyMap, 0, tagsObject[tagId].dependencies);
                if (tagSelectable) {
                    tags.push({
                        id:   tagId,
                        name: tagsObject[tagId].name
                    });
                }
            });

            tags = tags.sort((a, b) => a.name.localeCompare(b.name));
            if (tags.length > 0) {
                // add empty value for deselection
                tags.unshift({
                    id:   '',
                    name: ''
                });
            }

            return tags;
        },
        processTagDependencies(map, index, dependencies)
        {
            let key = map[index++];
            let currentValues = this[key];

            if (!Array.isArray(currentValues)) {
                currentValues = currentValues.split(',');
            }

            let result = true;
            // dependencies leaf is an array of component / repair codes
            if (Array.isArray(dependencies)) {

                let allValuesFound = true;
                currentValues.forEach(currentValue => {
                    allValuesFound = allValuesFound && dependencies.includes(currentValue);
                });

                result = true;

            } else {

                currentValues.forEach(currentValue => {
                    if (
                        typeof dependencies[currentValue] === 'undefined'
                        && typeof dependencies[''] === 'undefined'
                    ) {
                        result = false;
                    } else {
                        result = result
                            && this.processTagDependencies(map, index, dependencies[currentValue] || dependencies['']);
                    }
                });

            }

            return result;
        },
        validateCostValue() {
            // remove all chars that are not 0-9 , and .
            this.materialCostValue = this.materialCostValue.replace(/[^\d.,]+/g, "");
            return true;
        }
    },
    computed: {
        changed() {
            // check if data was changed (initial data === current data)
            let newData  = this.collectData();
            let allEqual = true;

            Object.keys(newData).some((field) => {
                let equal = this.selectedValues[field] === newData[field]
                allEqual = allEqual && equal;
                return !equal;
            });

            return !allEqual;
        },
        widthTypes() {
            return ['equal'];
        },
        mode() {
            let item = {
                surveyType:    this.viewMode === 'editor' ? this.surveyType : this.surveyType.join(','),
                serviceType:   this.viewMode === 'editor' ? this.serviceType : this.serviceType.join(','),
                viewType:      this.viewType,
                componentCode: this.componentCode,
                repairCode:    this.viewMode === 'editor' ? this.repairCode : this.repairCode.join(',')
            };

            return replacementTrait.modeDisplay(item, this.equipment, this.selectableValues.modes, this.modeReplacements);
        },
        selectableViewTypes() {
            let selectableViewTypes = [];
            this.selectableValues.viewTypes.forEach((item) => {
                if (this.selectableValues.viewTypeSplits[item.id]) {
                    // do split & add data
                    for (let key in this.selectableValues.viewTypeSplits[item.id]) {
                        selectableViewTypes.push({
                            id:   item.id + '-' + key,
                            name: this.selectableValues.viewTypeSplits[item.id][key]
                        });
                    }
                } else {
                    selectableViewTypes.push(item);
                }
            });

            return selectableViewTypes;
        },
        selectableMachineryTypes()
        {
            return this.$convertObjectToObjectsInArray(this.selectableValues.machineryTypes[this.machineryManufacturer] || {});
        },
        selectableStsCode1()
        {
            let selectableCodesWithDependency = {};
            let addCodeToName = true;

            if (
                this.mode // mode will be calculated when initial data is loaded, editor view is sometimes faster than that
                && !this.mappingDisabled
            ) {

                let selectableCodes = {};
                let usedMode = this.mode.split(',')[0];
                if (this.stsCodes[1][this.equipment][usedMode]) {
                    selectableCodes = this.stsCodes[1][this.equipment][usedMode];
                }

                if (this.isMachinery) {
                    // apply mode, machinery manufacturer & machinery type dependency
                    let machineryType = this.machineryType ? this.machineryType : '';
                    let machineryManufacturer = this.machineryManufacturer.length === 1 ? this.machineryManufacturer[0] : 'XXX';

                    if (
                        this.selectableDependencies.stsCode1
                        && this.selectableDependencies.stsCode1[usedMode]
                        && this.selectableDependencies.stsCode1[usedMode][machineryManufacturer]
                        && this.selectableDependencies.stsCode1[usedMode][machineryManufacturer][machineryType]
                    ) {
                        this.selectableDependencies.stsCode1[usedMode][machineryManufacturer][machineryType].forEach(
                            stsCode1 => selectableCodesWithDependency[stsCode1] = stsCode1 + ' - ' + selectableCodes[stsCode1]
                        );
                    }

                    if (this.isMachinery) {

                        if (this.machineryManufacturer.length > 1) {
                            // machinery has no view type mapping
                            let setCodes = [0, 6, 8, 9];
                            selectableCodesWithDependency = {};
                            setCodes.forEach((code) => selectableCodesWithDependency[code] = code);

                            selectableCodesWithDependency['X'] = 'X - ' + i18n.t('Multiple');
                        }
                    }

                    addCodeToName = false;

                } else {
                    let viewType = this.viewType;
                    // view type split does it make a bit tricky here
                    // add position type to view type to get the correct values
                    if (this.positionType) {
                        viewType += '-' + this.positionType;
                    }

                    // apply mode & view type dependency
                    if (
                        this.selectableDependencies.stsCode1
                        && this.selectableDependencies.stsCode1[usedMode]
                        && this.selectableDependencies.stsCode1[usedMode][viewType]
                    ) {
                        this.selectableDependencies.stsCode1[usedMode][viewType].forEach(stsCode1 => {
                            selectableCodesWithDependency[stsCode1] = selectableCodes[stsCode1];
                        });
                    }
                }

            } else {
                addCodeToName = false;
                selectableCodesWithDependency = this.getUnmappedStsCodes();

                if (
                    Array.isArray(this.machineryManufacturer)
                    && this.machineryManufacturer.length > 1
                ) {
                    selectableCodesWithDependency['X'] = 'X - ' + i18n.t('Multiple');
                }
            }

            return this.$convertObjectToObjectsInArray(
                selectableCodesWithDependency,
                'id',
                'name',
                addCodeToName
            );
        },
        selectableStsCode2()
        {
            let selectableCodes = {};
            let addCodeToName = true;

            if (
                this.mappingDisabled
                || this.isMachinery
            ) {
                addCodeToName   = false;
                selectableCodes = this.getUnmappedStsCodes();
            } else {
                if (
                    this.stsCodes[1]
                    && this.stsCodes[1][this.equipment][this.mode]
                    && this.stsCodes[2][this.equipment][this.mode][this.stsCode1]
                ) {
                    selectableCodes = this.stsCodes[2][this.equipment][this.mode][this.stsCode1];
                }
            }

            return this.$convertObjectToObjectsInArray(selectableCodes, 'id', 'name', addCodeToName);
        },
        selectableStsCode3()
        {
            let selectableCodes = {};
            let addCodeToName = true;

            if (
                this.mappingDisabled
                || this.isMachinery
            ) {
                addCodeToName   = false;
                selectableCodes = this.getUnmappedStsCodes();
            } else {
                if (
                    this.stsCodes[3]
                    && this.stsCodes[3][this.equipment][this.mode]
                    && this.stsCodes[3][this.equipment][this.mode][this.stsCode1]
                    && this.stsCodes[3][this.equipment][this.mode][this.stsCode1][this.stsCode2]
                ) {
                    selectableCodes = this.stsCodes[3][this.equipment][this.mode][this.stsCode1][this.stsCode2];
                }
            }

            return this.$convertObjectToObjectsInArray(selectableCodes, 'id', 'name', addCodeToName);
        },
        selectableStsCode4()
        {
            // there are no code 4 dependencies mapped yet
            let selectableCodes = this.getUnmappedStsCodes();
            return this.$convertObjectToObjectsInArray(selectableCodes, 'id', 'name');
        },
        showDetailedPosition()
        {
            return this.detailedPositionsByComponent[this.componentCode]; // component has detailed positions
        },
        showMachineryType()
        {
            return this.selectableMachineryTypes.length > 0
        },
        isMachinery()
        {
            return this.equipment === equipments.MACHINERY;
        },
        modeExceptionHint()
        {
            let texts = [];
            this.modeReplacements.forEach(replacement => {
                if (replacement.equipment !== this.equipment) {
                    return true;
                }

                let codes = [];

                let surveyTypeNames = [];
                this.selectableValues.surveyTypes.forEach(surveyTypeItem => {
                    if (replacement.surveyTypes.includes(parseInt(surveyTypeItem.id))) {
                        surveyTypeNames.push(surveyTypeItem.name)
                    }
                });
                codes.push(surveyTypeNames.join(', '));

                let viewTypeNames = [];
                this.selectableValues.viewTypes.forEach(viewTypeItem => {
                    if (replacement.viewTypes.includes(parseInt(viewTypeItem.id))) {
                        viewTypeNames.push(viewTypeItem.name)
                    }
                });
                codes.push(viewTypeNames.join(', '));

                codes.push(replacement.componentCodes.join(', '));
                codes.push(replacement.repairCodes.join(', '));

                let hintText = i18n.t(
                    'The mode for combination {codes} will be replaced with {mode}.',
                    {
                        codes: codes.join(' - '),
                        mode:  replacement.modeReplacement
                    }
                );

                texts.push(hintText);
            });

            if (texts.length) {
                texts = texts.join('\n');
            } else {
                texts = 'There are no mode replacements for this equipment.'
            }

            return texts;
        },
        selectableComponentTags()
        {
            return this.computeTagDependencies('component');
        },
        selectableDamageTags()
        {
            return this.computeTagDependencies('damage');
        },
        selectableRepairTags()
        {
            return this.computeTagDependencies('repair');
        }
    },
    watch: {
        // watcher to recalculate perimeter parameters
        lengthType() {
            this.calculatePerimeter();
        },
        lengthFrom() {
            this.calculatePerimeter();
        },
        lengthTo() {
            this.calculatePerimeter();
        },
        widthType() {
            this.calculatePerimeter();
        },
        widthFrom() {
            this.calculatePerimeter();
        },
        widthTo() {
            this.calculatePerimeter();
        },
        perimeterFrom() {
            this.calculatePerimeter();
        },
        metricType() {
            if (this.metricType !== 'Length' && this.metricType !== 'Perimeter') {
                this.lengthType = '';
                this.lengthFrom = '';
                this.lengthTo = '';
            }
            if (this.metricType !== 'Perimeter') {
                this.widthFrom = '';
                this.widthTo = '';
                this.perimeterFrom = '';
                this.perimeterTo = '';
            }
            if (this.metricType !== 'Quantity') {
                this.quantityType = '';
                this.quantityFrom = '';
                this.quantityTo   = '';
            }
        },
        componentCode() {
            if (!this.showDetailedPosition) {
                this.detailedPosition = '';
            }
            if (!this.refrigerantComponents.includes(this.componentCode)) {
                this.refrigerant = '';
            }
        },
        machineryManufacturer() {
            if (
                this.machineryManufacturer.length > 1
                || this.selectableMachineryTypes.length === 0
            ) {
                this.machineryType = '';
            } else {
                this.fixStsCode1();
            }
        },
        machineryType() {
            this.fixStsCode1();
        },
        $data: {
            handler: function() {
                this.$emit('changed', this.changed, this.collectData());
            },
            deep: true
        },
        selectableStsCode1() {
            // auto select sts code 1 if only one selection is available
            if (this.selectableStsCode1.length === 1) {
                this.stsCode1 = this.selectableStsCode1[0].id;
            }
        }
    },
    mounted() {
        this.calculatePerimeter();
    }
}
</script>

<style scoped>
    div.replacement-editor-merc-root {
        width: 35%;
        min-width: 500px;
        border-left: 1px solid var(--sc-blue);
        text-align: left;
        font-size: var(--font-size-editor);
    }

    div.replacement-editor-header {
        width: 100%;
        height: 56px;
        padding: 16px;
        background-color: var(--table-header-background);
    }

    div.replacement-editor-header > div.editor-header-text {
        color: var(--toolbar-label);
        font-size: var(--font-size-headline);
    }

    div.replacement-editor-header > div.editor-header-close {
        width: 24px;
        height: 24px;
        float: right;
    }

    /* Editor lines */
    div.replacement-editor-box {

    }
    div.editor-line {
        padding: 16px;
        border-bottom: 1px solid var(--table-header-border)
    }
    div.editor-line.slim-editor-line {
        padding: 4px 16px;
    }
    div.editor-block {
        display: inline-block;
        vertical-align: top;
    }
    div.editor-block img.icon {
        position: absolute;
        bottom: 4px;
        right: 0;
        height: 18px;
        width: 18px;
        display: block;
        cursor: pointer;
    }
    div.left-block {
        width: 60%;
    }
    div.right-block {
        width: 36%;
    }
    div.input-line { width: 100% }
    div.input-field {}
    div.input-field div.label {
        color: var(--toolbar-label);
    }
    div.input-field div.label.disabled {
        color: var(--toolbar-select-underline-disabled);
    }
    div.input-field div.label.required:after {
        content: ' *';
    }

    div.ribbon-select-select {
        font-size: var(--font-size-editor);
    }

    div.cedex-code-caption {
        color: var(--text-dark);
        font-size: var(--font-size-headline);
        margin: 0 0 16px;
    }

    div.select {
        width: 240px;
    }

    div.length-type {
        width: 96px;
    }

    div.length {
        width: 56px;
    }

    div.centered-input >>> input {
        text-align: center;
    }

    div.greater >>> div.v-input__slot:before {
        content: '≥ ';
        margin-bottom: 13px;
    }

    div.smaller >>> div.v-input__slot:before {
        content: '≤ ';
        margin-bottom: 13px;
    }

    div.length-block {
        display: inline-block;
        margin-right: 8px;
    }

    div.problem {
        color: var(--error-dark-red);
        font-weight: bold;
    }

    div.no-label {
        vertical-align: bottom;
    }

    div.width-25 {
        width: 24%;
        padding-right: 8px;
        margin-right: 0;
    }
</style>
<style>
    div.replacement-editor-merc-root div.select.multi-select div.v-select__selections {
        height: 20px;
        overflow: hidden;
    }

    div.replacement-editor-merc-root div.select.multi-select div.v-select__selection--comma {
        display: inline;
    }

    div.replacement-editor-merc-root div.ribbon-button-two-state {
        margin-bottom: 8px;
    }
    div.replacement-editor-merc-root div.ribbon-button-two-state > div.ribbon-button-text {
        text-align: right;
        padding-right: 8px;
        width: 80px;
        font-size: var(--font-size-editor);
    }

    div.v-input.ribbon-select-select.v-text-field input {
        padding: 2px 0 14px 0;
    }

    /* disable default setting */
    div.editor-line.slim-editor-line div.v-input {
        margin-top: 0;
    }
    div.editor-line.slim-editor-line div.v-input div.v-messages {
        display: none;
    }
    div.editor-line.slim-editor-line div.v-input--checkbox label.v-label {
        color: rgba(0, 0, 0, 0.87);
    }
</style>